import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Pagination,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Tooltip,
  Button,
} from "@mui/material";
import CleaningServicesIcon from "@mui/icons-material/CleaningServicesTwoTone";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { getFeedbacks } from "../services/feedbackService";
import { getAllBusinesses } from "../services/adminService";
import { useMessage } from "../contexts/MessageContext";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../utils/formatDate";
import {
  SentimentDissatisfied as MoodIcon,
  BatteryAlert as EnergyIcon,
  Poll as WellnessIcon,
  Home as EnvironmentIcon,
  Stars as RecognitionIcon,
  Comment as CommentIcon,
  Work as WorkloadIcon,
  TrendingUp as EngagementIcon,
  Group as TeamIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassIcon from "@mui/icons-material/HourglassBottom";

const ViewFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [startDate, setStartDate] = useState(""); // Default to empty
  const [endDate, setEndDate] = useState(""); // Default to empty
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { showMessage } = useMessage();
  const { user } = useAuth();

  // Load businesses for admins
  const loadBusinesses = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      if (user.role === "admin") {
        const data = await getAllBusinesses();
        setBusinesses(data);
      }
    } catch (error) {
      showMessage("Failed to fetch businesses.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [user, showMessage]);

  // Load feedbacks for selected business
  const loadFeedbacks = useCallback(
    async (businessId, newPage = 1) => {
      if (!user || !businessId) return;
      setIsLoading(true);
      try {
        const data = await getFeedbacks(
          businessId,
          newPage,
          limit,
          startDate ? new Date(startDate).toISOString() : undefined, // Pass ISO date-time string only if startDate is not empty
          endDate ? new Date(endDate).toISOString() : undefined // Pass ISO date-time string only if endDate is not empty
        );
        setFeedbacks(data.feedback);
        setTotalFeedbacks(data.total);
        setPage(newPage);
      } catch (error) {
        showMessage("Failed to fetch feedbacks.", "error");
      } finally {
        setIsLoading(false);
        setDrawerOpen(false);
      }
    },
    [showMessage, user, limit, startDate, endDate]
  );

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const applyDateFilter = useCallback(() => {
    if (selectedBusiness) {
      loadFeedbacks(selectedBusiness.id, 1);
    }
  }, [selectedBusiness, loadFeedbacks]);

  useEffect(() => {
    if (selectedBusiness) {
      applyDateFilter();
    }
  }, [startDate, endDate, applyDateFilter, selectedBusiness]);

  // Fetch businesses & feedback on mount
  useEffect(() => {
    loadBusinesses();
    if (user?.role === "businessOwner") {
      setSelectedBusiness(user);
      loadFeedbacks(user.id);
    }
  }, [loadBusinesses, user, loadFeedbacks]);

  // Handle page change
  const handlePageChange = (_, newPage) => {
    if (selectedBusiness) {
      loadFeedbacks(selectedBusiness.id, newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
    if (selectedBusiness) {
      loadFeedbacks(selectedBusiness.id, 1);
    }
  };

  const iconMapping = {
    moodCheck: MoodIcon,
    energyLevel: EnergyIcon,
    wellnessPoll: WellnessIcon,
    workEnvironment: EnvironmentIcon,
    recognition: RecognitionIcon,
    expressYourself: CommentIcon,
    workloadSatisfaction: WorkloadIcon,
    engagement: EngagementIcon,
    teamDynamicsInsight: TeamIcon,
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        padding: { xs: "1rem", md: "2rem" },
        mt: { xs: 4, md: 0 },
      }}
    >
      {/* Drawer Button for Admins */}
      {user?.role === "admin" && !drawerOpen && (
        <IconButton
          color="primary"
          onClick={() => setDrawerOpen(true)}
          sx={{
            position: "fixed",
            top: { xs: 65, md: 80 },
            left: { xs: 16, md: 20 },
            zIndex: 2000,
            backgroundColor: (theme) => theme.palette.accent.main,
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Business Selection Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "80%", md: 300 },
            boxSizing: "border-box",
            backgroundColor: "#fafafa",
            top: 60,
          },
        }}
      >
        <Box sx={{ padding: "1rem" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Select a Business</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ marginBottom: "0.5rem" }} />
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                height: "50vh",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {businesses.map((business) => (
                <React.Fragment key={business.id}>
                  <ListItem
                    button
                    onClick={() => {
                      setSelectedBusiness(business);
                      loadFeedbacks(business.id);
                    }}
                    selected={selectedBusiness?.id === business.id}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedBusiness?.id === business.id
                          ? "#e0f7fa"
                          : "inherit",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    <ListItemText
                      primary={business.name}
                      secondary={business.email}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Heading and Filters */}
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 3 }}>
        {selectedBusiness ? `${selectedBusiness.name}'s Feedback` : "Select a Business"}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 3,
          gap: 2,
        }}
      >
        {/* Date-Time Filters with Clear Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <TextField
            label="Start Date & Time"
            type="datetime-local"
            size="small"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date & Time"
            type="datetime-local"
            size="small"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {/* Clear Filters Button */}
          {(startDate || endDate) && (
            <Tooltip title="Clear Filters">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  applyDateFilter(); // Reload feedbacks without date filters
                }}
                startIcon={<CleaningServicesIcon fontSize="small" />}
                sx={{
                  borderRadius: "8px",
                  padding: "4px 10px",
                  minWidth: "90px", // Keeps it compact but visible
                  textTransform: "capitalize", // Keeps it readable
                  fontSize: "0.75rem", // Smaller text
                }}
              >
                Clear
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Pagination Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: { xs: "center", sm: "left" },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Showing {Math.min((page - 1) * limit + 1, totalFeedbacks)}–
            {Math.min(page * limit, totalFeedbacks)} of {totalFeedbacks} records
          </Typography>
          <FormControl
            size="small"
            sx={{ minWidth: 150, width: { xs: "100%", sm: "auto" } }}
          >
            <InputLabel id="limit-select-label">Records per page</InputLabel>
            <Select
              labelId="limit-select-label"
              value={limit}
              onChange={handleLimitChange}
              label="Records per page"
              fullWidth={true}
            >
              {[5, 10, 20, 50, 100, 250, 500].map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Feedback Cards */}
      {isLoading ? (
        <CircularProgress sx={{ display: "block", margin: "auto" }} />
      ) : feedbacks.length > 0 ? (
        <Grid container spacing={3}>
          {feedbacks.map((feedback) => (
            <Grid item xs={12} sm={6} md={4} key={feedback.id}>
              <Card
                sx={{
                  height: "100%",
                  p: 2,
                  borderRadius: "12px",
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  marginBottom: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                }}
              >
                <CardContent>
                  {/* Feedback ID */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        color: "primary.main",
                      }}
                    >
                      Feedback ID: {feedback.id}
                    </Typography>

                    {feedback.status === "completed" ? (
                      <CheckCircleIcon
                        sx={{ color: "green", fontSize: "1.5rem" }}
                      />
                    ) : (
                      <HourglassIcon
                        sx={{ color: "orange", fontSize: "1.5rem" }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Feedback Details */}
                  {Object.entries(feedback.data).map(([key, value]) => {
                    const Icon = iconMapping[key] || CommentIcon;

                    // Handle wellnessPoll as an array
                    const displayValue =
                      key === "wellnessPoll" && Array.isArray(value)
                        ? value.join(", ")
                        : value;

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 1.5,
                          gap: 1.5,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: { xs: "1.2rem", sm: "1.5rem" },
                            color: "primary.main",
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "600",
                              fontSize: { xs: "0.85rem", sm: "0.9rem" },
                              color: "#333",
                              textTransform: "capitalize",
                            }}
                          >
                            {key}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.8rem" },
                              color: "#555",
                              mt: 0.5,
                              wordBreak: "break-word",
                            }}
                          >
                            {displayValue}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>

                {/* Submission Date */}
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      color: "#888",
                      fontStyle: "italic",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Submitted on: {formatDate(feedback.created_at)}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography
          variant="h6"
          sx={{ textAlign: "center", marginTop: "2rem" }}
        >
          {user?.role === "admin"
            ? businesses.length > 0
              ? selectedBusiness
                ? totalFeedbacks > 0
                  ? "Displaying feedback records for the selected business."
                  : "No feedback records found for the selected business."
                : "Select a business from the menu to view its detailed feedback."
              : "No businesses found. Please add a business to view its feedback stats."
            : totalFeedbacks > 0
            ? "Displaying feedback records for your business."
            : "No feedback stats available for your business."}
        </Typography>
      )}

      {/* Pagination */}
      {feedbacks.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={Math.ceil(totalFeedbacks / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default ViewFeedback;
